import { Head, useForm, usePage } from "@inertiajs/react";
import {
  AlertTriangle,
  Building2,
  DollarSign,
  FileText,
  Upload,
} from "lucide-react";
import type { FormEventHandler } from "react";
import { useState } from "react";
import Dropzone from "shadcn-dropzone";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import CurrencyFormatHint from "@/components/currency-format-hint";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "@/components/ui/field";
import { FilePreview } from "@/components/ui/file-preview";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, PurchaseRequest, RequestItem, SharedData } from "@/types";

interface NewManualQuoteProps {
  purchase_request: PurchaseRequest & {
    sector: {
      id: number;
      name: string;
    };
  };
  request_items: RequestItem[];
}

// Form structure for quote items
interface QuoteItemForm {
  request_item_id: number;
  quantity: number;
  unit_price: number | null;
  context_notes: string;
}

export default function NewManualQuote({
  purchase_request,
  request_items,
}: NewManualQuoteProps) {
  const shared = usePage<SharedData>().props;
  const organizationSlug = shared.auth?.current_organization?.slug ?? "";
  const purchaseRequestsHref = `/organizations/${organizationSlug}/purchase_requests`;
  const purchaseRequestHref = `${purchaseRequestsHref}/${purchase_request.id}`;
  const purchaseRequestQuotesHref = `${purchaseRequestHref}/quotes`;
  const supplierSearchEndpoint = `/organizations/${organizationSlug}/suppliers/search`;
  const purchaseRequestDisplayId =
    purchase_request.request_number ?? purchase_request.id;
  const [selectedSupplier, setSelectedSupplier] = useState<AsyncOption | null>(
    null,
  );

  const { data, setData, post, processing, errors, transform } = useForm<{
    supplier_id: number | null;
    backup_files: File[];
    currency: "ARS" | "USD";
    exchange_rate: number | null;
    quote_items: QuoteItemForm[];
  }>({
    supplier_id: null,
    backup_files: [],
    currency: "ARS",
    exchange_rate: null,
    quote_items: request_items.map((item) => ({
      request_item_id: item.id,
      quantity: item.quantity,
      unit_price: null,
      context_notes: "",
    })),
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Solicitudes Compras",
      href: purchaseRequestsHref,
    },
    {
      title: `Solicitud #${purchaseRequestDisplayId}`,
      href: purchaseRequestHref,
    },
    {
      title: "Cotización Manual",
      href: "#",
    },
  ];

  const loadSupplierOptions = async (
    inputValue: string,
  ): Promise<AsyncOption[]> => {
    const params = new URLSearchParams();
    if (inputValue) {
      params.set("term", inputValue);
    }

    try {
      const response = await fetch(`${supplierSearchEndpoint}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
        },
      });
      if (!response.ok) {
        return [];
      }
      const json = (await response.json()) as AsyncOption[];
      return json.map((option) => ({
        value: String(option.value),
        label: option.label,
      }));
    } catch {
      return [];
    }
  };

  const MAX_FILE_COUNT = 10;

  const handleBackupFilesChange = (acceptedFiles: File[]) => {
    // Calculate how many more files we can add
    const remainingSlots = MAX_FILE_COUNT - data.backup_files.length;
    if (remainingSlots <= 0) {
      return; // Already at max
    }
    // Only add files up to the limit
    const filesToAdd = acceptedFiles.slice(0, remainingSlots);
    setData("backup_files", [...data.backup_files, ...filesToAdd]);
  };

  const removeBackupFile = (index: number) => {
    setData(
      "backup_files",
      data.backup_files.filter((_, i) => i !== index),
    );
  };

  const handleItemChange = (
    index: number,
    field: keyof QuoteItemForm,
    value: string | number | null,
  ) => {
    const newItems = [...data.quote_items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    setData("quote_items", newItems);
  };

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    // Filter items with price
    const itemsWithPrice = data.quote_items.filter(
      (item) => item.unit_price !== null && item.unit_price > 0,
    );

    // Transform to FormData with Rails-compatible structure
    transform((formState) => {
      const formData = new FormData();

      if (formState.supplier_id) {
        formData.append("quote[supplier_id]", String(formState.supplier_id));
      }

      // Append backup files (PDF or images)
      formState.backup_files.forEach((file) => {
        formData.append("quote[backup_files][]", file);
      });

      // Add currency and exchange_rate
      formData.append("quote[currency]", formState.currency);
      if (formState.currency === "USD" && formState.exchange_rate !== null) {
        formData.append(
          "quote[exchange_rate]",
          String(formState.exchange_rate),
        );
      }

      // Append quote items with explicit indices for Rails nested attributes
      itemsWithPrice.forEach((item, index) => {
        formData.append(
          `quote[quote_items][${index}][request_item_id]`,
          String(item.request_item_id),
        );
        formData.append(
          `quote[quote_items][${index}][quantity]`,
          String(item.quantity),
        );
        formData.append(
          `quote[quote_items][${index}][unit_price]`,
          String(item.unit_price),
        );
        if (item.context_notes) {
          formData.append(
            `quote[quote_items][${index}][context_notes]`,
            item.context_notes,
          );
        }
      });

      return formData;
    });

    post(purchaseRequestQuotesHref);
  };

  const hasAtLeastOnePrice = data.quote_items.some(
    (item) => item.unit_price !== null && item.unit_price > 0,
  );
  const hasExchangeRateIfNeeded =
    data.currency === "ARS" ||
    (data.currency === "USD" &&
      data.exchange_rate !== null &&
      data.exchange_rate > 0);
  const hasBackupFiles = data.backup_files.length > 0;
  const isValid =
    data.supplier_id &&
    hasBackupFiles &&
    hasAtLeastOnePrice &&
    hasExchangeRateIfNeeded;

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`Cotización Manual - Solicitud #${purchaseRequestDisplayId}`}
      />

      <div className="flex h-full flex-1 flex-col p-4 md:p-6">
        <div className="w-full max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold">Crear Cotización Manual</h1>
            <p className="text-muted-foreground mt-1">
              Solicitud de compra #{purchaseRequestDisplayId} -{" "}
              {purchase_request.sector.name}
            </p>
          </div>

          {/* Show base errors from server */}
          {(errors as Record<string, string>).base && (
            <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
              {(errors as Record<string, string>).base}
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <FieldSet className="rounded-lg border bg-card p-6 shadow-sm">
              <FieldLegend className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5" />
                Datos de la cotización
              </FieldLegend>
              <FieldDescription>
                Selecciona el proveedor, adjunta el PDF de respaldo y completa
                los precios cotizados.
              </FieldDescription>
              <FieldGroup>
                <Field data-invalid={!!errors.supplier_id}>
                  <FieldLabel>Proveedor *</FieldLabel>
                    <FieldDescription>
                    Si no encuentras el proveedor en el listado, debes crearlo primero en la sección de Proveedores y luego volver a intentar aquí.
                    </FieldDescription>
                  <AsyncCombobox
                    value={selectedSupplier}
                    onChange={(opt) => {
                      setSelectedSupplier(opt);
                      setData("supplier_id", opt ? Number(opt.value) : null);
                    }}
                    loadOptions={loadSupplierOptions}
                    defaultOptions={true}
                    cacheOptions={true}
                    placeholder="Buscar proveedor..."
                    searchPlaceholder="Escribe para buscar proveedores..."
                  />
                  <FieldError>{errors.supplier_id}</FieldError>
                </Field>

                <Field>
                  <FieldLabel className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Moneda de la cotización *
                  </FieldLabel>
                  <RadioGroup
                    value={data.currency}
                    onValueChange={(value: "ARS" | "USD") => {
                      setData("currency", value);
                      if (value === "ARS") {
                        setData("exchange_rate", null);
                      }
                    }}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="ARS" id="currency-ars" />
                      <Label htmlFor="currency-ars" className="cursor-pointer">
                        Pesos Argentinos (ARS)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="USD" id="currency-usd" />
                      <Label htmlFor="currency-usd" className="cursor-pointer">
                        Dólares (USD)
                      </Label>
                    </div>
                  </RadioGroup>
                </Field>

                {data.currency === "USD" && (
                  <Field
                    data-invalid={
                      !!(errors as Record<string, string>).exchange_rate
                    }
                  >
                    <FieldLabel>Tipo de Cambio (1 USD = X ARS) *</FieldLabel>
                    <FieldDescription>
                      Ingresa el tipo de cambio para convertir los precios a
                      pesos argentinos.
                    </FieldDescription>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Ej: 1200.00"
                      value={data.exchange_rate ?? ""}
                      onChange={(e) =>
                        setData(
                          "exchange_rate",
                          e.target.value ? parseFloat(e.target.value) : null,
                        )
                      }
                      disabled={processing}
                      className="max-w-xs"
                    />
                    <FieldError>
                      {(errors as Record<string, string>).exchange_rate}
                    </FieldError>
                  </Field>
                )}

                <Field data-invalid={!!(errors as Record<string, string>).backup_files}>
                  <FieldLabel className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Documento de Cotización *
                  </FieldLabel>
                    <FieldDescription>
                    Adjunta un archivo de respaldo (PDF o imágenes) que documente la cotización del proveedor. Si subís varias imágenes, se combinan en un PDF. Este archivo no se procesa por IA, pero es requerido como evidencia.
                    </FieldDescription>
                  <Dropzone
                    onDrop={handleBackupFilesChange}
                    accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                    maxFiles={10}
                    disabled={processing}
                    containerClassName="w-full"
                    dropZoneClassName="w-full h-32 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    {(dz) => (
                      <div className="text-center p-4">
                        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {dz.isDragActive
                            ? "Suelta los archivos aquí"
                            : "Arrastra el documento de la cotización o haz clic"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, JPG, PNG · Si la cotización tiene varias páginas, subí todas (hasta 10 archivos)
                        </p>
                      </div>
                    )}
                  </Dropzone>

                  {data.backup_files.length > 0 && (
                    <div className="grid gap-2 mt-2">
                      {data.backup_files.map((file, index) => (
                        <FilePreview
                          key={index}
                          file={file}
                          onRemove={() => removeBackupFile(index)}
                          disabled={processing}
                        />
                      ))}
                    </div>
                  )}

                  <FieldError>{(errors as Record<string, string>).backup_files}</FieldError>
                </Field>
              </FieldGroup>

              <FieldSeparator>Ítems y precios</FieldSeparator>

              <FieldGroup>
                <Field>
                  <FieldLabel className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Ítems y Precios
                  </FieldLabel>
                  <FieldDescription>
                    Completa los precios unitarios para los ítems cotizados.
                    Deja en blanco los ítems no cotizados.
                  </FieldDescription>

                  {data.currency === "USD" && (
                    <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 dark:text-amber-200">
                        Ingresa los precios en dólares (USD), no en pesos. El
                        sistema usará el tipo de cambio indicado para calcular
                        el equivalente en pesos.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="rounded-lg border">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[300px]">
                              Descripción
                            </TableHead>
                            <TableHead className="min-w-[100px]">
                              Cantidad
                            </TableHead>
                            <TableHead className="min-w-[100px]">
                              Unidad
                            </TableHead>
                            <TableHead className="min-w-[150px]">
                              Precio Unitario *
                            </TableHead>
                            <TableHead className="min-w-[200px]">
                              Notas (Opcional)
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {request_items.map((requestItem, index) => (
                            <TableRow key={requestItem.id}>
                              <TableCell className="whitespace-normal break-words">
                                {requestItem.description}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={
                                    data.quote_items[index]?.quantity ??
                                    requestItem.quantity
                                  }
                                  onChange={(e) =>
                                    handleItemChange(
                                      index,
                                      "quantity",
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  disabled={processing}
                                  className="w-full"
                                />
                              </TableCell>
                              <TableCell className="whitespace-normal break-words">
                                {requestItem.unit}
                              </TableCell>
                              <TableCell>
                                <CurrencyInput
                                  currency={data.currency}
                                  value={
                                    data.quote_items[index]?.unit_price ?? null
                                  }
                                  onValueChange={(numericValue) =>
                                    handleItemChange(
                                      index,
                                      "unit_price",
                                      numericValue,
                                    )
                                  }
                                  placeholder="0,00"
                                  disabled={processing}
                                  showHint={false}
                                  className="w-full"
                                />
                              </TableCell>
                              <TableCell>
                                <TextareaWithCounter
                                  value={
                                    data.quote_items[index]?.context_notes ?? ""
                                  }
                                  onChange={(e) =>
                                    handleItemChange(
                                      index,
                                      "context_notes",
                                      e.target.value,
                                    )
                                  }
                                  disabled={processing}
                                  rows={2}
                                  maxLength={200}
                                  showCharacterCount={false}
                                  placeholder="Agregar notas..."
                                  className="w-full"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <FieldDescription className="mt-2">
                    * Completa al menos un precio unitario para crear la
                    cotización.
                  </FieldDescription>
                  <div className="mt-2">
                    <CurrencyFormatHint currency={data.currency} />
                  </div>
                </Field>
              </FieldGroup>
            </FieldSet>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!isValid || processing}>
                {processing ? "Guardando..." : "Crear Cotización"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}
